import React, { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AdjustedProductType, ProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { producerCommissionOverrideService } from "../../../services/commissions/ProducerCommissionOverrideService";
import { useAuthStore } from "../../../stores/authStore";
import { findProposedFees } from "../../../utils/labUtils";
import { formatCurrency, formatPercentage } from "../../../utils/namesFormater";
import PercentageTextField from "../../../shared/UIParts/InputFields/PercentageTextField";

// products without a fee on deposits - only a saving fee
const NO_DEPOSIT_FEE_PRODUCTS: number[] = [
  AdjustedProductType.EDUCATION_FUND,
  AdjustedProductType.PURE_SAVINGS_POLICY,
  AdjustedProductType.INVESTMENT_PROVIDENT_FUND,
];

export const hasDepositFee = (productType: number | null | undefined) =>
  productType == null || !NO_DEPOSIT_FEE_PRODUCTS.includes(productType);

/**
 * The saving fee the agent proposes at a handler - the handler's agreement for the client's total there
 * (the agreements loaded as the lab loads them). null - no handler / no agreement.
 */
export const useProposedSavingFee = (handlerCode: string, totalAtHandler: number): number | null => {
  const userId = useAuthStore((state) => state.user?.id);
  const { data: overrides = [] } = useQuery({
    queryKey: ["producerCommissionOverrides", userId, ProductType.PROVIDENT_FUND],
    queryFn: () => producerCommissionOverrideService.fetchByUserId(userId!, ProductType.PROVIDENT_FUND),
    enabled: !!userId,
  });
  return useMemo(() => {
    if (!handlerCode) return null;
    const handlerOverrides = overrides
      .filter((o) => o.handler_code === handlerCode)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return handlerOverrides.length > 0 ? findProposedFees(totalAtHandler, handlerOverrides) : null;
  }, [handlerCode, overrides, totalAtHandler]);
};

// a fee field - empty is "unchanged / unknown" (null), not 0
const FeeField: React.FC<{ label: string; value: number | null; onChange: (v: number | null) => void; helperText?: string }> = ({
  label,
  value,
  onChange,
  helperText,
}) => (
  <PercentageTextField
    nullable
    label={label}
    value={value}
    onChange={onChange}
    decimalPlaces={2}
    max={10}
    size="medium"
    sx={{ maxWidth: 170 }}
    helperText={helperText}
  />
);

interface Props {
  productType: number | null | undefined;
  proposedSavingFee: number | null;
  savingFee: number | null;
  depositFee: number | null;
  onSavingFeeChange: (fee: number | null) => void;
  onDepositFeeChange: (fee: number | null) => void;
  /** the current saving fee (e.g. of the transferred funds) - compared with the new one */
  currentSavingFee?: number | null;
  /** the money the fee change applies to - for the estimated annual difference */
  amountForDiff?: number;
}

/** דמי ניהול בקופה: מצבירה (מוצע לפי הסכם העמלות של היצרן) ומהפקדה (רק במוצרים שיש בהם), ניתנים לשינוי */
const TargetFeesFields: React.FC<Props> = ({
  productType,
  proposedSavingFee,
  savingFee,
  depositFee,
  onSavingFeeChange,
  onDepositFeeChange,
  currentSavingFee = null,
  amountForDiff = 0,
}) => {
  const annualDiff =
    currentSavingFee != null && savingFee != null ? ((currentSavingFee - savingFee) / 100) * amountForDiff : null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        דמי ניהול
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        <FeeField
          label="מצבירה"
          value={savingFee}
          onChange={onSavingFeeChange}
          helperText={proposedSavingFee != null ? `מוצע לפי ההסכם: ${formatPercentage(proposedSavingFee)}` : "אין הסכם עמלות ליצרן"}
        />
        {hasDepositFee(productType) && <FeeField label="מהפקדה" value={depositFee} onChange={onDepositFeeChange} />}
      </Box>
      {currentSavingFee != null && savingFee != null && (
        <Alert severity={annualDiff != null && annualDiff >= 0 ? "success" : "warning"} sx={{ mt: 1 }}>
          דמי ניהול מצבירה: {formatPercentage(currentSavingFee)} ← {formatPercentage(savingFee)}
          {annualDiff != null && annualDiff !== 0 && (
            <>
              {" "}
              · {annualDiff > 0 ? "חיסכון" : "תוספת"} שנתית משוערת של {formatCurrency(Math.abs(annualDiff))}
            </>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default TargetFeesFields;
